/**
 * Platform Admin management-command runner API.
 *
 * Its own module rather than an addition to `core.ts`: this is a distinct
 * domain (platform operations) and `core.ts` is already past the 500-line
 * limit.
 *
 * A command must be BOTH registered in the backend registry and enabled on
 * this install before it can be queued — see
 * `backend/core/management_runner/registry.py`.
 */
import api from './client';
import { PaginatedResponse } from './client';

/** One declared argument of a runnable command. */
export interface CommandArgSpec {
  /** `call_command` kwarg name, e.g. `order_types`. */
  name: string;
  /** Literal switch, e.g. `--order-types`. Shown to the operator. */
  flag: string;
  label: string;
  type: 'str' | 'int' | 'date' | 'bool' | 'choice';
  required: boolean;
  default: string | number | boolean | null;
  choices: string[];
  help: string;
}

export interface RunnableCommandSpec {
  key: string;
  label: string;
  description: string;
  /** Mutates data — the UI demands typed confirmation before a real run. */
  destructive: boolean;
  /** How to undo it, shown verbatim. Empty means there is no undo. */
  undo_hint: string;
  /** Switched on for THIS install. Disabled commands cannot be queued. */
  enabled: boolean;
  args: CommandArgSpec[];
}

export type CommandRunStatus = 'queued' | 'running' | 'success' | 'failed';

/** A headline figure rendered as a tile above the results table. */
export interface CommandResultTile {
  label: string;
  value: string | number;
  tone?: 'ok' | 'warn' | 'bad';
}

export interface CommandResultColumn {
  key: string;
  label: string;
  align?: 'left' | 'right';
}

export interface CommandResultTable {
  columns: CommandResultColumn[];
  rows: Record<string, string | number | null>[];
  /** Real count — `rows` may be a sample. */
  total: number;
}

/**
 * Lets one command hand a list straight into another's argument, so an
 * operator never copies a comma-separated blob by hand.
 */
export interface CommandResultHandoff {
  label: string;
  /** Registry key of the command to load. */
  command: string;
  /** Argument name on that command to fill. */
  field: string;
  values: (string | number)[];
  /** Arguments to carry across unchanged (customer, order types...). */
  carry?: Record<string, string | number | boolean>;
}

/** Structured findings a command published. Empty when it only printed prose. */
export interface CommandResult {
  summary?: CommandResultTile[];
  table?: CommandResultTable;
  handoff?: CommandResultHandoff;
}

export interface CommandRun {
  id: number;
  command_key: string;
  command_label: string;
  arguments: Record<string, string | number | boolean>;
  status: CommandRunStatus;
  result: CommandResult;
  requested_by: number | null;
  requested_by_name: string;
  stdout: string;
  stderr: string;
  error: string;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
  duration_seconds: number | null;
  is_finished: boolean;
}

const BASE = '/core/command-runner';

export const commandRunnerAPI = {
  /** Every registered command, each flagged enabled or not on this install. */
  getCommands: () =>
    api.get<{ commands: RunnableCommandSpec[] }>(`${BASE}/commands/`),

  /** Enable or disable a command for this install. */
  toggleCommand: (key: string, enabled: boolean) =>
    api.post<{ key: string; enabled: boolean; enabled_commands: string[] }>(
      `${BASE}/commands/toggle/`,
      { key, enabled },
    ),

  /** Queue a run. Resolves once queued, not once finished — poll `getRun`. */
  createRun: (
    commandKey: string,
    args: Record<string, string | number | boolean>,
  ) =>
    api.post<CommandRun>(`${BASE}/runs/`, {
      command_key: commandKey,
      arguments: args,
    }),

  /** Poll one run for status and captured output. */
  getRun: (id: number) => api.get<CommandRun>(`${BASE}/${id}/`),

  /** Run history, newest first. */
  getRuns: (params?: Record<string, string | number>) =>
    api.get<PaginatedResponse<CommandRun>>(`${BASE}/`, { params }),
};
